/**
 * Thin client for the per-step signup-wizard API. Mirrors backend
 * STEP_SCHEMAS dispatch — each step has its own typed payload.
 *
 * Telemetry contract: every step fires `start` on mount and either
 * `complete` or `skip` on advance. Back navigation fires `back`. The
 * activation funnel is built from these events server-side.
 */
import api from '../../services/api';
import type { PracticeArea } from '../../types/practiceArea';

// Mirrors backend StepName Literal exactly. Add a new step to BOTH the
// backend STEP_SCHEMAS and this union — frontend tests will fail-fast.
// NOTE: the 'persona' (Simple/Lawyer/Judge mode) step was removed 2026-07-28 —
// lawyers-only product, mode is always 'lawyer'. PersonaStepPayload is kept
// below only as the shape the legacy /preferences path uses.
export type StepName =
  | 'practice_area'
  | 'professional_identity'
  | 'firm_shape'
  | 'intent_and_source';

// ----- step payloads (must match backend Pydantic schemas) -----

export interface PracticeAreaStepPayload {
  /**
   * Ordered list of the lawyer's practice areas. `practice_areas[0]` is the
   * PRIMARY anchor (persisted as `users.primary_practice_area`); the rest are
   * secondary areas the lawyer also practises in. Backend requires ≥1 item.
   */
  practice_areas: PracticeArea[];
}

export type YearsExperienceBucket = '0-2' | '3-5' | '6-10' | '11-20' | '20+';
export type PrimaryCourt = 'district' | 'high_court' | 'supreme_court' | 'tribunal' | 'none_yet';

export interface ProfessionalIdentityStepPayload {
  full_name: string;
  bar_council_id?: string | null;
  years_experience: YearsExperienceBucket;
  primary_court: PrimaryCourt;
}

export type FirmType =
  | 'solo'
  | 'small_firm_2_10'
  | 'mid_firm_11_50'
  | 'large_firm_50plus'
  | 'in_house'
  | 'government'
  | 'student';

export interface FirmShapeStepPayload {
  firm_type: FirmType;
  firm_name?: string | null;
  city: string;
  state: string;
}

export type PrimaryIntent =
  | 'case_research'
  | 'drafting_documents'
  | 'client_management'
  | 'precedent_search'
  | 'bns_ipc_mapping'
  | 'bail_calculation'
  | 'fir_analysis'
  | 'learning_new_laws'
  | 'other';

export type AcquisitionSource =
  | 'google_search'
  | 'referral_friend'
  | 'social_linkedin'
  | 'social_twitter'
  | 'bar_association'
  | 'legal_blog'
  | 'youtube'
  | 'event_conference'
  | 'other';

export interface IntentAndSourceStepPayload {
  primary_intents: PrimaryIntent[];   // 1..3 items
  acquisition_source: AcquisitionSource;
  referral_code?: string | null;
}

export type UserMode = 'lawyer' | 'judge' | 'layperson';

export interface PersonaStepPayload {
  user_mode: UserMode;
  language?: string | null;
}

// Map of step → payload type. Used by the orchestrator to keep step state
// well-typed across the wizard.
export type StepPayload =
  | { step: 'practice_area';         data: PracticeAreaStepPayload }
  | { step: 'professional_identity'; data: ProfessionalIdentityStepPayload }
  | { step: 'firm_shape';            data: FirmShapeStepPayload }
  | { step: 'intent_and_source';     data: IntentAndSourceStepPayload };

// ----- responses -----

export interface StepEventResponse {
  event_id: number;
  step_name: StepName;
  next_step: StepName | null;
}

export interface SignupStateResponse {
  user_id: string;
  is_onboarded: boolean;
  completed_steps: StepName[];
  current_step: StepName | null;
}

export interface SignupFinalizeResponse {
  is_onboarded: boolean;
  finalized_at: string;
}

// ----- API surface -----

export const signupApi = {
  start: (step_name: StepName) =>
    api.post<StepEventResponse>('/signup/step/start', { step_name }),

  complete: (
    step_name: StepName,
    payload: Record<string, unknown>,
    time_on_step_ms?: number,
  ) =>
    api.post<StepEventResponse>('/signup/step/complete', {
      step_name,
      payload,
      time_on_step_ms,
    }),

  skip: (step_name: StepName, time_on_step_ms?: number) =>
    api.post<StepEventResponse>('/signup/step/skip', { step_name, time_on_step_ms }),

  back: (from_step: StepName, to_step: StepName) =>
    api.post<StepEventResponse>('/signup/step/back', { from_step, to_step }),

  state: () =>
    api.get<SignupStateResponse>('/signup/state'),

  finalize: () =>
    api.post<SignupFinalizeResponse>('/signup/finalize'),
};

// Step ordering — single source of truth on the frontend, mirrors
// backend REQUIRED_STEPS exactly.
export const STEP_ORDER: readonly StepName[] = [
  'practice_area',
  'professional_identity',
  'firm_shape',
  'intent_and_source',
] as const;

// Steps where "Skip for now" is hidden (functional gates).
export const NON_SKIPPABLE_STEPS: ReadonlySet<StepName> = new Set([
  'practice_area',
]);
